<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import BaseIcon from "./BaseIcon.vue";

export interface ContextMenuItem {
    label: string;
    icon?: string;
    danger?: boolean;
    disabled?: boolean;
    action: () => void;
}

export interface ContextMenuState {
    x: number;
    y: number;
    items: ContextMenuItem[];
}

const props = withDefaults(defineProps<{
    menu: ContextMenuState | null;
    teleportTo?: string;
}>(), {
    teleportTo: "#app",
});

const emit = defineEmits<{
    (event: "close"): void;
}>();

const menuRef = ref<HTMLElement | null>(null);

watch(() => props.menu, async (menu) => {
    if (!menu) return;
    await nextTick();
    const element = menuRef.value;
    if (!element) return;

    const { innerWidth, innerHeight } = window;
    const { offsetWidth, offsetHeight } = element;
    if (menu.x + offsetWidth > innerWidth) element.style.left = `${innerWidth - offsetWidth - 8}px`;
    if (menu.y + offsetHeight > innerHeight) element.style.top = `${innerHeight - offsetHeight - 8}px`;
});

function onItemClick(item: ContextMenuItem) {
    if (item.disabled) return;
    item.action();
    emit("close");
}

function onMousedown(event: MouseEvent) {
    if (!(event.target as HTMLElement).closest("[data-context-menu]")) emit("close");
}

watch(() => props.menu, (menu) => {
    if (menu) window.addEventListener("mousedown", onMousedown, true);
    else window.removeEventListener("mousedown", onMousedown, true);
}, { immediate: true });

onBeforeUnmount(() => window.removeEventListener("mousedown", onMousedown, true));
</script>

<template>
    <Teleport :to="teleportTo">
        <div
            v-if="menu"
            ref="menuRef"
            data-context-menu
            class="fixed z-[9999] min-w-[160px] py-1 rounded-lg shadow-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-dark-100 text-sm"
            :style="{ left: `${menu.x}px`, top: `${menu.y}px` }"
        >
            <template v-for="(item, index) in menu.items" :key="index">
                <div v-if="item.label === '---'" class="my-1 border-t border-gray-100 dark:border-white/8" />
                <button
                    v-else
                    class="w-full flex items-center gap-2.5 px-3 py-1.5 transition-colors text-left"
                    :class="[
                        item.disabled
                            ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                            : item.danger
                                ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-200',
                    ]"
                    :disabled="item.disabled"
                    @click="onItemClick(item)"
                >
                    <BaseIcon v-if="item.icon" :name="item.icon" :size="13" class="shrink-0 opacity-70" />
                    <span>{{ item.label }}</span>
                </button>
            </template>
        </div>
    </Teleport>
</template>
